/**
 * DUPLICATES, ALL AT ONCE: every group of near-copy features in the room, each
 * around the feature to keep, with the copies to fold into it ticked.
 *
 * The long tail of the feature cloud is mostly one feature written five ways.
 * Merging them a feature page at a time was a press and a reload per copy;
 * here a person looks down the groups, fixes what is wrong, and presses once.
 *
 * Three ways to say what is the same:
 *  - the word rule (`reports.feature_twins`) finds the groups and ticks the
 *    close ones, and names that mean nearly the same join them unticked
 *    (features with an embedding only);
 *  - "Ask AI to check" reads the groups on screen (or all of them) and sets
 *    the ticks as its proposal, saying why (`boardroom.twin_review`) - nothing
 *    is merged until the button is pressed;
 *  - "Join by hand" finds any features by name, for the pairs no word rule
 *    would see.
 */
import { useEffect, useRef, useState } from 'react';
import type { BoardroomFeatureTwinGroup, BoardroomFeatureTwins } from '../../../types';

/** Groups shown at once, and how many more each "show more" adds. */
const GROUPS_SHOWN = 30;
/** Groups sent to the AI per request. Checking every group goes as several of
 *  these one after another, so each request stays short and the panel can show
 *  how far it has got. */
export const AI_GROUPS_PER_ASK = 30;

type Verdict = { same: boolean; why: string };
type Hit = { id: number; title: string };

export interface TwinsApi {
  load: () => Promise<BoardroomFeatureTwins>;
  merge: (groups: { into: number; items: number[] }[]) => Promise<{ merged: number; failed: { id: number; error: string }[] }>;
  review: (
    groups: { keep: { id: number; title: string }; members: { id: number; title: string }[] }[],
  ) => Promise<{ verdicts: Record<string, Verdict>; answered: number; failed_batches: number }>;
  keepApart: (a: number, b: number) => Promise<unknown>;
  /** Features whose title holds these words. */
  search: (q: string) => Promise<Hit[]>;
}

/** Where a ticked copy goes: the copy it was pointed at, if that one is in
 *  its group and is itself staying (not ticked); otherwise the keeper. So
 *  "financial reporting" and "Revenue Reporting" can merge with each other
 *  inside a "reporting" group while "compliance reporting" stays apart. Pure. */
export function targetOf(
  g: BoardroomFeatureTwinGroup,
  memberId: number,
  picked: ReadonlySet<number>,
  into: Readonly<Record<number, number>>,
): number {
  const t = into[memberId];
  if (t != null && t !== memberId && !picked.has(t) && g.members.some(m => m.id === t)) return t;
  return g.keep.id;
}

/** Two names that read the same once case and spacing are set aside. */
export function sameName(a: string, b: string): boolean {
  const norm = (x: string) => x.toLowerCase().replace(/\s+/g, ' ').trim();
  return norm(a) === norm(b);
}

/** What one press would merge: each ticked copy into its target, gathered by
 *  target. Pure. */
export function mergePlan(
  groups: BoardroomFeatureTwinGroup[],
  picked: ReadonlySet<number>,
  into: Readonly<Record<number, number>> = {},
) {
  const out: { into: number; items: number[] }[] = [];
  for (const g of groups) {
    const by = new Map<number, number[]>();
    for (const m of g.members) {
      if (!picked.has(m.id)) continue;
      const t = targetOf(g, m.id, picked, into);
      by.set(t, [...(by.get(t) ?? []), m.id]);
    }
    by.forEach((items, target) => out.push({ into: target, items }));
  }
  return out;
}

/** The ticks the word rule starts with: the close ones. Pure. */
export function likelyPicks(groups: BoardroomFeatureTwinGroup[]): Set<number> {
  const out = new Set<number>();
  for (const g of groups) for (const m of g.members) if (m.likely) out.add(m.id);
  return out;
}

/** The ticks with the AI's answers laid over them. Pure. */
export function withVerdicts(picked: ReadonlySet<number>, verdicts: Readonly<Record<number, Verdict>>): Set<number> {
  const next = new Set(picked);
  Object.entries(verdicts).forEach(([id, v]) => (v.same ? next.add(Number(id)) : next.delete(Number(id))));
  return next;
}

/** What the AI made of one group: how many of its copies it answered, and how
 *  many answers differ from the word rule's starting tick. `changed` 0 with
 *  every copy checked is the AI agreeing with the group as found. Pure. */
export function aiGroupStatus(
  g: BoardroomFeatureTwinGroup,
  verdicts: Readonly<Record<number, Verdict>>,
): { checked: number; of: number; changed: number } {
  let checked = 0;
  let changed = 0;
  for (const m of g.members) {
    const v = verdicts[m.id];
    if (!v) continue;
    checked += 1;
    if (v.same !== m.likely) changed += 1;
  }
  return { checked, of: g.members.length, changed };
}

/** The groups the AI has not answered in full yet, so a second press never
 *  pays for the same group twice. Pure. */
export function uncheckedGroups(
  groups: BoardroomFeatureTwinGroup[],
  verdicts: Readonly<Record<number, Verdict>>,
): BoardroomFeatureTwinGroup[] {
  return groups.filter(g => {
    const st = aiGroupStatus(g, verdicts);
    return st.checked < st.of;
  });
}

/** A group with a copy made its keeper, and the old keeper a copy. Pure. */
export function swapKeeper(g: BoardroomFeatureTwinGroup, memberId: number): BoardroomFeatureTwinGroup {
  const m = g.members.find(x => x.id === memberId);
  if (!m) return g;
  return {
    keep: { id: m.id, title: m.title, products: m.products },
    members: [
      { id: g.keep.id, title: g.keep.title, products: g.keep.products, score: m.score, likely: true },
      ...g.members.filter(x => x.id !== memberId),
    ],
  };
}

export default function FeatureTwinsPanel({ api, onOpen }: { api: TwinsApi; onOpen: (id: number) => void }) {
  const [groups, setGroups] = useState<BoardroomFeatureTwinGroup[] | null>(null);
  const [totals, setTotals] = useState<BoardroomFeatureTwins['totals'] | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  // A ticked copy pointed at another copy in its group, not the keeper.
  const [into, setInto] = useState<Record<number, number>>({});
  const [ai, setAi] = useState<Record<number, Verdict>>({});
  // The answers so far, for a reload to lay back over the fresh ticks.
  const aiRef = useRef(ai);
  aiRef.current = ai;
  // Set to stop a "check all" between requests.
  const stopAsking = useRef(false);
  const [shown, setShown] = useState(GROUPS_SHOWN);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const reload = () =>
    api.load().then(d => {
      setGroups(d.groups);
      setTotals(d.totals);
      // A merge reloads the list: the AI's answers for the groups still here
      // stay as the ticks, rather than going back to the word rule's.
      setPicked(withVerdicts(likelyPicks(d.groups), aiRef.current));
      setInto({});
    });

  useEffect(() => {
    reload().catch(() => setNote('Could not read the duplicates.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!groups || !totals) {
    return <p className="text-xs text-gray-400">{note ?? 'Looking for duplicates…'}</p>;
  }

  const visible = groups.slice(0, shown);
  const shownLeft = uncheckedGroups(visible, ai);
  const allLeft = uncheckedGroups(groups, ai);
  const plan = mergePlan(groups, picked, into);
  // Point a copy at another copy: that one stays, so it is unticked.
  const pointAt = (memberId: number, target: number) => {
    setInto(i => ({ ...i, [memberId]: target }));
    setPicked(p => {
      const next = new Set(p);
      next.delete(target);
      return next;
    });
  };
  const copies = plan.reduce((a, g) => a + g.items.length, 0);
  const toggle = (id: number) =>
    setPicked(p => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const run = async (label: string, fn: () => Promise<string | null>) => {
    setBusy(label);
    setNote(null);
    try {
      setNote(await fn());
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  const merge = (which: { into: number; items: number[] }[]) =>
    run('merge', async () => {
      const out = await api.merge(which);
      await reload();
      return `Merged ${out.merged} into ${which.length} feature${which.length === 1 ? '' : 's'}.${
        out.failed.length ? ` ${out.failed.length} could not be merged.` : ''
      }`;
    });

  const askAi = (todo: BoardroomFeatureTwinGroup[]) =>
    run('ai', async () => {
      stopAsking.current = false;
      let asked = 0;
      let answered = 0;
      let failed = 0;
      let done = 0;
      for (let i = 0; i < todo.length; i += AI_GROUPS_PER_ASK) {
        if (stopAsking.current) break;
        const chunk = todo.slice(i, i + AI_GROUPS_PER_ASK);
        if (todo.length > AI_GROUPS_PER_ASK) setNote(`The AI is checking groups ${i + 1}–${i + chunk.length} of ${todo.length}…`);
        const out = await api.review(
          chunk.map(g => ({
            keep: { id: g.keep.id, title: g.keep.title },
            members: g.members.map(m => ({ id: m.id, title: m.title })),
          })),
        );
        const got: Record<number, Verdict> = {};
        Object.entries(out.verdicts).forEach(([k, v]) => {
          got[Number(k)] = v;
        });
        setAi(a => ({ ...a, ...got }));
        // THE AI'S ANSWERS ARE THE TICKS NOW - a proposal; nothing is merged.
        setPicked(p => withVerdicts(p, got));
        asked += chunk.reduce((a, g) => a + g.members.length, 0);
        answered += out.answered;
        failed += out.failed_batches;
        done += chunk.length;
      }
      const missed = asked - answered;
      return [
        `The AI checked ${answered} of ${asked} near-copies in ${done} group${done === 1 ? '' : 's'} and set the ticks.`,
        done < todo.length ? `Stopped; ${todo.length - done} groups not checked.` : '',
        missed > 0 ? `${missed} it did not answer keep their ticks${failed ? ' (part of the request failed)' : ''}.` : '',
      ]
        .filter(Boolean)
        .join(' ');
    });

  const different = (g: BoardroomFeatureTwinGroup, memberId: number) =>
    run('apart', async () => {
      await api.keepApart(g.keep.id, memberId);
      setGroups(gs =>
        (gs ?? [])
          .map(x => (x.keep.id === g.keep.id ? { ...x, members: x.members.filter(m => m.id !== memberId) } : x))
          .filter(x => x.members.length > 0),
      );
      return null;
    });

  return (
    <div className="flex flex-col gap-3 text-xs" data-testid="feature-twins">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60">
        <span className="text-gray-700 dark:text-gray-200" data-testid="feature-twins-totals">
          {totals.groups} group{totals.groups === 1 ? '' : 's'} of near-copies · {totals.copies} copies of {totals.features} features
        </span>
        {(totals.embedded ?? 0) < totals.features && (
          <span className="w-full text-[11px] text-gray-500 dark:text-gray-400" data-testid="feature-twins-embedded">
            {totals.embedded
              ? `Matched by meaning for ${totals.embedded} of ${totals.features} features so far; the rest by shared words only.`
              : 'Matched by shared words only: no feature has been read for meaning yet.'}
          </span>
        )}
        <span className="ml-auto flex flex-wrap gap-2">
          {busy === 'ai' ? (
            <button
              type="button"
              onClick={() => (stopAsking.current = true)}
              className="rounded border border-violet-300 px-2 py-1 text-violet-800 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-900/20"
              data-testid="feature-twins-ai-stop"
            >
              Reading… stop
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={busy !== null || shownLeft.length === 0}
                onClick={() => askAi(shownLeft)}
                className="rounded border border-violet-300 px-2 py-1 text-violet-800 hover:bg-violet-50 disabled:opacity-50 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-900/20"
                title="An AI reads the groups shown and sets the ticks, saying why. Nothing is merged until you press Merge."
                data-testid="feature-twins-ai"
              >
                {shownLeft.length ? `Ask AI to check these ${shownLeft.length} groups` : 'AI has checked the groups shown'}
              </button>
              {allLeft.length > shownLeft.length && (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => askAi(allLeft)}
                  className="rounded border border-violet-300 px-2 py-1 text-violet-800 hover:bg-violet-50 disabled:opacity-50 dark:border-violet-700 dark:text-violet-200 dark:hover:bg-violet-900/20"
                  title={`Checks every group not yet checked, ${AI_GROUPS_PER_ASK} at a time. Charged like the other AI checks; you can stop it part way.`}
                  data-testid="feature-twins-ai-all"
                >
                  Check all {allLeft.length} groups
                </button>
              )}
            </>
          )}
          <button
            type="button"
            disabled={busy !== null || copies === 0}
            onClick={() => merge(plan)}
            className="rounded border border-indigo-300 bg-indigo-50 px-2 py-1 font-medium text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 dark:hover:bg-indigo-900/60"
            data-testid="feature-twins-merge-all"
          >
            {busy === 'merge' ? 'Merging…' : `Merge all ticked (${copies} into ${plan.length})`}
          </button>
        </span>
        {note && (
          <p className="w-full text-gray-600 dark:text-gray-300" data-testid="feature-twins-note">
            {note}
          </p>
        )}
      </div>

      <JoinByHand api={api} disabled={busy !== null} onMerge={(into, items) => merge([{ into, items }])} />

      {visible.map(g => {
        const groupPlan = mergePlan([g], picked, into);
        const mine = groupPlan.flatMap(x => x.items);
        // How many copies are joining each copy that stays.
        const joining = new Map<number, number>();
        for (const x of groupPlan) if (x.into !== g.keep.id) joining.set(x.into, x.items.length);
        return (
          <section
            key={g.keep.id}
            className="rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800"
            data-testid={`twin-group-${g.keep.id}`}
          >
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="rounded bg-emerald-50 px-1.5 text-[11px] text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">keep</span>
              <button type="button" onClick={() => onOpen(g.keep.id)} className="font-medium text-gray-900 hover:underline dark:text-gray-100">
                {g.keep.title}
              </button>
              <span className="text-[11px] text-gray-400">
                {g.keep.products} product{g.keep.products === 1 ? '' : 's'}
              </span>
              <AiGroupBadge status={aiGroupStatus(g, ai)} />
              <button
                type="button"
                disabled={busy !== null || mine.length === 0}
                onClick={() => merge(groupPlan)}
                className="ml-auto rounded border border-indigo-200 px-1.5 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40 disabled:hover:bg-transparent dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
                data-testid={`twin-merge-${g.keep.id}`}
              >
                Merge {mine.length || ''} here
              </button>
            </div>
            <ul className="flex flex-col gap-0.5 pl-1">
              {g.members.map(m => {
                const v = ai[m.id];
                return (
                  <li key={m.id} className="flex flex-wrap items-center gap-2" data-testid={`twin-member-${m.id}`}>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={picked.has(m.id)}
                      disabled={busy !== null}
                      onChange={() => toggle(m.id)}
                      aria-label={`Merge ${m.title} into ${g.keep.title}`}
                      data-testid={`twin-pick-${m.id}`}
                    />
                    <button type="button" onClick={() => onOpen(m.id)} className="text-gray-800 hover:underline dark:text-gray-200">
                      {m.title}
                    </button>
                    <span className="text-[11px] text-gray-400">
                      {m.products} product{m.products === 1 ? '' : 's'}
                    </span>
                    {/* THE SAME NAME TWICE is two rows, not one row merging into
                        itself: two seats added it at the same moment. Its number
                        tells the two apart. */}
                    {sameName(m.title, g.keep.title) && (
                      <span
                        className="rounded bg-amber-50 px-1 text-[11px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                        title={`A second copy of #${g.keep.id} with exactly the same name (this one is #${m.id})`}
                        data-testid={`twin-same-${m.id}`}
                      >
                        same name · a second copy
                      </span>
                    )}
                    {m.match === 'meaning' && (
                      <span
                        className="rounded bg-sky-50 px-1 text-[11px] text-sky-800 dark:bg-sky-900/30 dark:text-sky-200"
                        title="No shared words: the names mean nearly the same"
                        data-testid={`twin-meaning-${m.id}`}
                      >
                        by meaning
                      </span>
                    )}
                    {picked.has(m.id) && (
                      <label className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                        into
                        <select
                          value={targetOf(g, m.id, picked, into)}
                          disabled={busy !== null}
                          onChange={e => pointAt(m.id, Number(e.target.value))}
                          className="rounded border border-gray-300 px-1 py-0.5 dark:border-gray-600 dark:bg-gray-700"
                          data-testid={`twin-into-${m.id}`}
                        >
                          <option value={g.keep.id}>{g.keep.title}</option>
                          {g.members
                            .filter(o => o.id !== m.id && !picked.has(o.id))
                            .map(o => (
                              <option key={o.id} value={o.id}>
                                {o.title}
                              </option>
                            ))}
                          {/* A ticked copy can be pointed at a copy that is
                              currently ticked too: picking it unticks it. */}
                          {g.members
                            .filter(o => o.id !== m.id && picked.has(o.id))
                            .map(o => (
                              <option key={o.id} value={o.id}>
                                {o.title} (keep it instead of merging)
                              </option>
                            ))}
                        </select>
                      </label>
                    )}
                    {joining.has(m.id) && (
                      <span className="rounded bg-emerald-50 px-1 text-[11px] text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200" data-testid={`twin-joining-${m.id}`}>
                        stays · {joining.get(m.id)} join it
                      </span>
                    )}
                    {v && (
                      <span
                        className={`text-[11px] ${v.same ? 'text-violet-700 dark:text-violet-300' : 'text-amber-700 dark:text-amber-300'}`}
                        data-testid={`twin-ai-${m.id}`}
                      >
                        AI: {v.same ? 'same' : 'different'}
                        {v.why ? ` - ${v.why}` : ''}
                      </span>
                    )}
                    <span className="ml-auto flex gap-2 text-[11px]">
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => {
                          setGroups(gs => (gs ?? []).map(x => (x.keep.id === g.keep.id ? swapKeeper(x, m.id) : x)));
                          setPicked(p => new Set(p).add(g.keep.id));
                        }}
                        className="text-gray-500 hover:underline disabled:opacity-40"
                        title="Keep this one and fold the others into it"
                      >
                        keep this instead
                      </button>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => different(g, m.id)}
                        className="text-gray-500 hover:underline disabled:opacity-40"
                        title="Not the same feature: never offer this pair again"
                        data-testid={`twin-apart-${m.id}`}
                      >
                        not the same
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      {groups.length > shown && (
        <button
          type="button"
          onClick={() => setShown(n => n + GROUPS_SHOWN)}
          className="self-start text-indigo-600 hover:underline dark:text-indigo-300"
        >
          show {Math.min(GROUPS_SHOWN, groups.length - shown)} more of {groups.length - shown}
        </button>
      )}
      {groups.length === 0 && <p className="text-gray-400">No near-copies left.</p>}
    </div>
  );
}

/** The AI's word on a whole group, beside its name. */
function AiGroupBadge({ status }: { status: { checked: number; of: number; changed: number } }) {
  if (!status.checked) return null;
  const all = status.checked === status.of;
  const text = !all
    ? `AI checked ${status.checked} of ${status.of}`
    : status.changed === 0
      ? 'AI checked · no changes needed'
      : `AI checked · changed ${status.changed} tick${status.changed === 1 ? '' : 's'}`;
  const ok = all && status.changed === 0;
  return (
    <span
      className={`rounded px-1.5 text-[11px] ${
        ok
          ? 'bg-violet-50 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200'
          : 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
      }`}
      data-testid="twin-ai-group"
    >
      {text}
    </span>
  );
}

/** FOR THE PAIRS NO WORD RULE SEES: find features by name, tick the ones that
 *  are one thing, pick which to keep, merge. */
function JoinByHand({
  api,
  disabled,
  onMerge,
}: {
  api: TwinsApi;
  disabled: boolean;
  onMerge: (into: number, items: number[]) => void;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [chosen, setChosen] = useState<Hit[]>([]);
  const [keep, setKeep] = useState<number | null>(null);
  // The latest api, without re-searching each time the page above re-renders.
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    const words = q.trim();
    if (words.length < 2) {
      setHits([]);
      return;
    }
    let alive = true;
    const t = window.setTimeout(() => {
      apiRef.current
        .search(words)
        .then(r => alive && setHits(r))
        .catch(() => alive && setHits([]));
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [q]);

  const add = (h: Hit) => {
    if (chosen.some(c => c.id === h.id)) return;
    setChosen(c => [...c, h]);
    setKeep(k => k ?? h.id);
  };
  const into = keep ?? chosen[0]?.id;

  return (
    <details className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700" data-testid="twin-join">
      <summary className="cursor-pointer text-gray-700 dark:text-gray-200">Join features by hand</summary>
      <div className="mt-2 flex flex-col gap-2">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Find features by name…"
          className="w-full max-w-md rounded border border-gray-300 px-2 py-1 dark:border-gray-600 dark:bg-gray-700"
          data-testid="twin-join-search"
        />
        {hits.length > 0 && (
          <ul className="flex flex-wrap gap-1">
            {hits
              .filter(h => !chosen.some(c => c.id === h.id))
              .map(h => (
                <li key={h.id}>
                  <button
                    type="button"
                    onClick={() => add(h)}
                    className="rounded-full border border-gray-300 px-1.5 py-0.5 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                    data-testid={`twin-join-hit-${h.id}`}
                  >
                    + {h.title}
                  </button>
                </li>
              ))}
          </ul>
        )}
        {chosen.length > 0 && (
          <div className="flex flex-col gap-1" data-testid="twin-join-chosen">
            <span className="text-gray-500">Pick the one to keep; the others fold into it.</span>
            {chosen.map(c => (
              <label key={c.id} className="flex items-center gap-2">
                <input type="radio" className="h-4 w-4" checked={into === c.id} onChange={() => setKeep(c.id)} />
                <span>{c.title}</span>
                <button
                  type="button"
                  onClick={() => {
                    setChosen(cs => cs.filter(x => x.id !== c.id));
                    if (keep === c.id) setKeep(null);
                  }}
                  className="icon-btn text-gray-400 hover:text-red-500"
                  aria-label={`Leave out ${c.title}`}
                >
                  ×
                </button>
              </label>
            ))}
            <button
              type="button"
              disabled={disabled || chosen.length < 2 || into == null}
              onClick={() => {
                onMerge(into!, chosen.filter(c => c.id !== into).map(c => c.id));
                setChosen([]);
                setKeep(null);
                setQ('');
              }}
              className="self-start rounded border border-indigo-300 bg-indigo-50 px-2 py-1 text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 dark:hover:bg-indigo-900/60"
              data-testid="twin-join-merge"
            >
              Merge {Math.max(0, chosen.length - 1)} into the one kept
            </button>
          </div>
        )}
      </div>
    </details>
  );
}
